import { useEffect, useRef } from 'react';

/**
 * Le retour système referme la surcouche du dessus — décision D6.
 *
 * Balayage depuis le bord sur iPhone, bouton ou geste retour sur Android, retour
 * du navigateur : tous produisent un `popstate`. Sans entrée d'historique propre
 * à la surcouche, ce retour quittait l'ÉCRAN, feuille ouverte comprise.
 *
 * **Une pile unique, pas une entrée par composant.** Chaque surcouche ouverte y
 * occupe un niveau ; l'historique porte autant d'entrées « surcouche » que la pile
 * a de niveaux, numérotées dans `history.state.tfSurcouche` (1, 2…). Tout se
 * ramène à comparer deux nombres — la hauteur de la pile et le niveau de l'entrée
 * courante — puis à pousser ou reculer de la différence :
 *
 * - **retour de l'utilisateur** : le niveau courant passe sous la pile, on ferme
 *   ce qui dépasse, du haut vers le bas — un niveau par retour ;
 * - **fermeture par la surcouche** (bouton, fond, Échap) : la pile passe sous le
 *   niveau courant, on recule d'autant — jamais d'entrée morte ;
 * - **une feuille qui en remplace une autre** dans le même rendu (« Outils » →
 *   « Messages rapides ») : la hauteur ne change pas, l'entrée est reprise ;
 * - **entrée orpheline** (on a navigué depuis une feuille, rechargé avec une
 *   feuille ouverte) : son niveau dépasse la pile, le retour la saute.
 *
 * La synchronisation est différée d'une microtâche : le double montage de
 * StrictMode et le relais entre deux feuilles s'y annulent avant tout
 * `pushState`. Les entrées poussées GARDENT l'état de React Router (`usr`, `key`,
 * `idx`) et l'URL : son calcul de delta et `navigate(-1)` n'y voient rien.
 */
const CLE = 'tfSurcouche';

const pile: { fermer: () => void }[] = [];
/** Un `history.go` à nous est en vol : son `popstate` ne doit rien fermer. */
let reculEnVol = false;
let synchroPrevue = false;

function niveauCourant(): number {
    const niveau = (window.history.state as Record<string, unknown> | null)?.[CLE];
    return typeof niveau === 'number' ? niveau : 0;
}

function synchroniser() {
    synchroPrevue = false;
    // Le `popstate` de notre recul relancera la synchronisation, sur le vrai niveau.
    if (reculEnVol) return;

    const niveau = niveauCourant();
    if (niveau > pile.length) {
        reculEnVol = true;
        window.history.go(pile.length - niveau);
        return;
    }
    for (let n = niveau + 1; n <= pile.length; n++) {
        window.history.pushState({ ...(window.history.state ?? {}), [CLE]: n }, '', window.location.href);
    }
}

function planifier() {
    if (synchroPrevue) return;
    synchroPrevue = true;
    queueMicrotask(synchroniser);
}

function surPopstate() {
    if (reculEnVol) {
        reculEnVol = false;
    } else {
        // Retour de l'utilisateur : ce qui dépasse le niveau atteint se ferme.
        const niveau = niveauCourant();
        while (pile.length > niveau) pile.pop()?.fermer();
    }
    synchroniser();
}

if (typeof window !== 'undefined') {
    window.addEventListener('popstate', surPopstate);
    // Démarrage sur une entrée de surcouche (rechargement, retour depuis une page
    // ouverte par une notification) : aucune surcouche n'est ouverte, on la saute.
    if (niveauCourant() > 0) planifier();
}

/**
 * Inscrit la surcouche dans la pile tant que `ouverte` est vrai. `fermer` est lu
 * au moment du retour : un rappel neuf à chaque rendu ne réinscrit rien.
 */
export function useFermetureParRetour(ouverte: boolean, fermer: () => void) {
    const refFermer = useRef(fermer);
    refFermer.current = fermer;

    useEffect(() => {
        if (!ouverte) return;
        const surcouche = { fermer: () => refFermer.current() };
        pile.push(surcouche);
        planifier();

        return () => {
            // Déjà retirée si c'est un retour qui l'a fermée.
            const index = pile.indexOf(surcouche);
            if (index >= 0) pile.splice(index, 1);
            planifier();
        };
    }, [ouverte]);
}
